import React, { useLayoutEffect, useMemo } from "react";
import "content-toggle-pattern/dist/main.css";
import minusCircle from "styleguide/icons/ds2/minuscircle.svg";
import plusCircle from "styleguide/icons/ds2/pluscircle.svg";
import { contentToggleInit } from "content-toggle-pattern/src/content-toggle";

export interface ContentToggleProps {
  /** The title text displayed in the expandable heading */
  titleText?: string;
  /** When false (default), applies fill-none class for no background fill */
  isFillOption?: boolean;
  /** When true, applies accordion behavior where only one section can be open */
  accordionOption?: boolean;
  /** Content to display inside the toggle section */
  children?: React.ReactNode;
}

/**
 * ContentToggle - An expandable/collapsible content section component
 * 
 * @component
 * @example
 * ```tsx
 * <ContentToggle titleText="Section Title" accordionOption={false}>
 *   <p>Your content here</p>
 * </ContentToggle>
 * ```
 */
const ContentToggle: React.FC<ContentToggleProps> = React.memo(({
  titleText,
  children,
  isFillOption = false,
  accordionOption = false,
}) => {
  // Memoize className construction for better performance
  const containerClassName = useMemo(() => {
    const classes = ['ds-contenttoggle'];
    
    if (accordionOption) {
      classes.push('ds-contenttoggle__behavior-accordion');
    }
    
    if (!isFillOption) {
      classes.push('ds-contenttoggle__fill-none');
    }
    
    return classes.join(' ');
  }, [accordionOption, isFillOption]);

  useLayoutEffect(() => {
    contentToggleInit("", "ds-contenttoggle");
  }, []);

  return (
    <div
      className={containerClassName}
      data-analytics-location="Content Toggle"
      data-ds-theme="vessel"
      data-ds-variant="basic"
      data-ds-version="2"
    >
      <button
        className="ds-contenttoggle__expandable-heading"
        data-analytics-type="Button"
        data-analytics-click="TITLE"
      >
        <img
          className="ds-contenttoggle__expandable-heading-icon ds-contenttoggle__plus-icon"
          alt="expand"
          height="24"
          width="24"
          src={plusCircle}
        />
        <img
          className="ds-contenttoggle__expandable-heading-icon ds-contenttoggle__minus-icon ds-contenttoggle__remove-icon"
          alt="expand"
          height="24"
          width="24"
          src={minusCircle}
        />
        <span className="-medium ds-contenttoggle__expandable-heading-text">
          {titleText}
        </span>
      </button>
      <div className="ds-contenttoggle__content">{children}</div>
    </div>
  );
});

ContentToggle.displayName = "ContentToggle";

export default ContentToggle;
